// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@conklin/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@conklin/App/Components/Cart/LinesHeader";
import QtyField, { QtyFieldStyles } from "@conklin/App/Components/Qty/QtyField";
import ProductAvailability, { ProductAvailabilityStyles } from "@conklin/Overrides/Components/ProductAvailability";
import { parseBoolean, xMask } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import setCanSendReturnRequest from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetCanSendReturnRequest";
import setOrderLines from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SetOrderLines";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartLinesListHeader from "@insite/content-library/Components/CartLinesListHeader";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Typography } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    orderLines: state.pages.requestRma.orderLines,
    // Xng-Changes
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    setOrderLines,
    setCanSendReturnRequest,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestRmaProductListStyles {
    gridContainer?: GridContainerProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    returnReasonGridItem?: GridItemProps;
    qtyToReturnTextField?: TextFieldPresentationProps;
    returnReasonSelect?: SelectPresentationProps;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productBrandStyles?: ProductBrandStyles;
    productInfoPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoSubtotalGridItem?: GridItemProps;
    // XNG-Changes
    priceStyles?: SmallHeadingAndTextStyles;
    productTotalPriceHeadingAndText?: SmallHeadingAndTextStyles;
    actionsContainer?: InjectableCss;
    priceText?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    priceWrapper?: InjectableCss;
    priceHeading?: TypographyProps;
    cartLineContainerStyles?: CartLineContainerStyles;
    linesHeader?: LinesHeaderStyles;
    productAvailability?: ProductAvailabilityStyles;
    productQty?: QtyFieldStyles;
    infoWrapper?: InjectableCss;
}

export const requestRmaProductListStyles: RequestRmaProductListStyles = {
    gridContainer: {
        css: css`
            margin-top: 35px;
            margin-bottom: 25px;
        `,
    },
    orderLineCardImageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
        css: css`
            padding-left: 0;
        `,
    },
    orderLineCardInfoGridItem: { width: [9, 9, 7, 7, 8] },
    orderLineCardInfoGridContainer: { gap: 10 },
    returnReasonGridItem: {
        width: [9, 9, 3, 3, 3],
        printWidth: 0,
        css: css`
            flex-direction: column;
            margin-left: auto !important;
        `,
    },
    qtyToReturnTextField: {
        cssOverrides: {
            formInputWrapper: css`
                width: 90px;
            `,
        },
    },
    productInfoGridItem: { width: [12, 12, 8, 8, 8] },
    productInfoGridContainer: { gap: 5 },
    productInfoBrandDescriptionGridItem: { width: 12 },
    productInfoBrandDescriptionGridContainer: { gap: 5 },
    productInfoBrandGridItem: { width: 12 },
    productInfoDescriptionGridItem: { width: 12 },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbers: {
        erpNumberLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        customerNameLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                margin-right: 8px;
            `,
        },
    },
    orderLineInfoGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
    },
    orderLineInfoGridContainer: { gap: 10 },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
    },
    productInfoSubtotalGridItem: { width: 12 },
};

// XNG-Changes: update structure
const ProductInfo = ({ orderLine }: { orderLine: OrderLineModel }) => {
    // XNG-Changes
    const orderLineInfo = OrderLineInfo({
        orderLine,
    });
    return (
        <>
            {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />}
            <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            <StyledWrapper {...styles.infoWrapper}>
                {/* XNG-Changes */}
                <ProductPartNumbers
                    productNumber={orderLine.productErpNumber}
                    customerProductNumber={orderLine.customerProductNumber}
                    manufacturerItem={orderLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                    // Xng-Changes
                    showCustomerName={false}
                    {...{ sku: orderLine.properties?.sku || "" }}
                />
                {/* XNG-Changes */}
                {orderLineInfo.Price}
            </StyledWrapper>
        </>
    );
};

// XNG Change: split qty, price and total in the same component
const OrderLineInfo = ({ orderLine }: { orderLine: OrderLineModel }) => {
    return {
        Price: (
            <StyledWrapper {...styles.priceWrapper}>
                {/* <Typography {...styles.priceHeading}>{translate("Price")}</Typography> */}
                <Typography {...styles.priceText}>{orderLine.unitPriceDisplay}</Typography>
                <Typography {...styles.unitOfMeasure}>
                    {orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : ""}
                </Typography>
            </StyledWrapper>
        ),
        Qty: <QtyField extendedStyles={styles.productQty} value={orderLine.qtyOrdered} editable={false} />,
        Total: (
            <SmallHeadingAndText
                {...styles.productTotalPriceHeadingAndText}
                heading={translate("Total")}
                text={orderLine.extendedUnitNetPriceDisplay}
            />
        ),
    };
};

const OrderLineCard = (props: {
    order: OrderModel;
    orderLine: OrderLineModel;
    totalQuantity: number;
    qtyChangeHandler: (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => void;
    returnReasonChangeHandler: (event: React.ChangeEvent<HTMLSelectElement>, lineNumber: number) => void;
    // Xng-Changes
    cart: Cart | undefined;
}) => {
    const { order, orderLine, totalQuantity, qtyChangeHandler, returnReasonChangeHandler, cart } = props;
    const minQtyToReturn = totalQuantity > 0 ? 0 : 1;
    const qtyToReturnError =
        totalQuantity > 0 &&
        ((orderLine.rmaQtyRequested > orderLine.qtyOrdered &&
            siteMessage("Field_Max_Number", translate("QTY Returning"), orderLine.qtyOrdered.toString())) ||
            (orderLine.rmaQtyRequested < minQtyToReturn &&
                siteMessage("Field_Min_Number", translate("QTY Returning"), minQtyToReturn.toString())));

    // XNG-Changes
    const orderLineInfo = OrderLineInfo({
        orderLine,
    });

    // XNG-Changes
    return (
        <CartLineContainer extendedStyles={styles.cartLineContainerStyles} includeActions={false}>
            <CartLineContainer.Image>
                {orderLine.productUri && orderLine.isActiveProduct && (
                    <Link href={orderLine.productUri} aria-label="Product Image">
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            altText={orderLine.altText}
                        />
                    </Link>
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <ProductInfo orderLine={orderLine} />
            </CartLineContainer.Description>
            <CartLineContainer.Inventory>
                {/* XNG-Changes */}
                {!parseBoolean(orderLine.properties.quoteRequired) && (
                    <ProductAvailability
                        productId={orderLine.productId!}
                        availability={orderLine.availability!}
                        unitOfMeasure={orderLine.unitOfMeasure}
                        trackInventory={parseBoolean(orderLine.properties.trackInventory)}
                        failedToLoadInventory={cart?.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                    />
                )}
            </CartLineContainer.Inventory>
            <CartLineContainer.Qty>{orderLineInfo.Qty}</CartLineContainer.Qty>
            <CartLineContainer.Total>{orderLineInfo.Total}</CartLineContainer.Total>
            <CartLineContainer.Actions>
                {/* XNG-Changes: fix actions vertical alignment and style on mobile */}
                <StyledWrapper {...styles.actionsContainer}>
                    <TextField
                        {...styles.qtyToReturnTextField}
                        label={translate("QTY to Return")}
                        // XNG-Changes: type qty
                        type="qty"
                        value={orderLine.rmaQtyRequested.toString()}
                        min={minQtyToReturn}
                        max={orderLine.qtyOrdered}
                        error={qtyToReturnError}
                        data-test-selector={`requestRmaLine_qtyToReturn_${orderLine.id}`}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            qtyChangeHandler(event, orderLine.lineNumber)
                        }
                    />
                    {!!order.returnReasons?.length && (
                        <Select
                            label={translate("Return Reason")}
                            {...styles.returnReasonSelect}
                            value={orderLine.returnReason || ""}
                            required={orderLine.rmaQtyRequested > 0}
                            error={
                                // XNG-Changes
                                !qtyToReturnError &&
                                orderLine.rmaQtyRequested > 0 &&
                                !orderLine.returnReason &&
                                siteMessage("Field_Required", translate("Return Reason"))
                            }
                            data-test-selector={`requestRmaLine_ReturnReason_${orderLine.id}`}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                returnReasonChangeHandler(event, orderLine.lineNumber)
                            }
                        >
                            <option value="">{translate("Select Option")}</option>
                            {order.returnReasons?.map(returnReason => (
                                <option key={returnReason} value={returnReason}>
                                    {returnReason}
                                </option>
                            ))}
                        </Select>
                    )}
                </StyledWrapper>
            </CartLineContainer.Actions>
        </CartLineContainer>
    );
};

const styles = requestRmaProductListStyles;

const RequestRmaProductList: FC<Props> = ({
    orderLines,
    setOrderLines,
    setCanSendReturnRequest,
    // Xng-Changes
    cart,
}) => {
    const [totalQuantity, setTotalQuantity] = React.useState(0);
    const { value: order } = useContext(OrderStateContext);

    React.useEffect(() => {
        if (!order || !order.orderLines) {
            return;
        }

        setOrderLines({ orderLines: order.orderLines });
    }, [order?.orderLines]);

    const reasonsDefined = !!order?.returnReasons?.length;
    React.useEffect(() => {
        setCanSendReturnRequest({
            value:
                orderLines.some(line => line.rmaQtyRequested > 0 && (line.returnReason || !reasonsDefined)) &&
                !orderLines.some(
                    line =>
                        line.rmaQtyRequested > line.qtyOrdered ||
                        (line.rmaQtyRequested > 0 && !line.returnReason && reasonsDefined),
                ),
        });
    }, [orderLines]);

    if (!order || !order.orderLines) {
        return null;
    }

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, lineNumber: number) => {
        let qty = parseInt(event.target.value, 10);
        if (event.target.value === "") {
            qty = 0;
        }
        const updatedOrderLines = orderLines.map(line =>
            line.lineNumber === lineNumber ? { ...line, rmaQtyRequested: qty } : line,
        );
        setOrderLines({ orderLines: updatedOrderLines });

        let totalQty = 0;
        updatedOrderLines.forEach(orderLine => {
            totalQty += orderLine.rmaQtyRequested > 0 ? 1 : 0;
        });

        setTotalQuantity(totalQty);
    };

    const returnReasonChangeHandler = (event: React.FormEvent<HTMLSelectElement>, lineNumber: number) => {
        setOrderLines({
            orderLines: orderLines.map(line =>
                line.lineNumber === lineNumber ? { ...line, returnReason: event.currentTarget.value } : line,
            ),
        });
    };

    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector="requestRmaProductList">
            {!!orderLines.length && (
                <CartLinesListHeader isCondensed={false} hideCondensedSelector productsCount={orderLines.length} />
            )}
            <LinesHeader extendedStyles={styles.linesHeader} includeActions={false} />
            {orderLines.map(orderLine => (
                <OrderLineCard
                    key={orderLine.id}
                    order={order}
                    orderLine={orderLine}
                    totalQuantity={totalQuantity}
                    qtyChangeHandler={qtyChangeHandler}
                    returnReasonChangeHandler={returnReasonChangeHandler}
                    // Xng-Changes
                    cart={cart}
                />
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestRmaProductList),
    definition: {
        allowedContexts: ["RequestRmaPage"],
        group: "Return Request (RMA)",
        fieldDefinitions: [],
    },
};

export default widgetModule;
